import { plural } from '../../../../shared/helpers';
import { queryClient } from '../../../ReactQuery';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { put } from '../../../redux/api';
import type { RemoveUsersFromTeamRequest, RemoveUsersFromTeamResponse } from '../../../../shared/types/routes/teams';
import type { Team } from '../../../types';

import { getTeamQueryKey } from './useTeam';
import { getBaseTeamMembersQueryKey } from './useTeamMembers';

type RemoveUsersFromTeamVariables = {
  teamId: number;
  users: Array<{ id: number }>;
};

//  Actual function to remove users from a team
async function removeUsersFromTeam({ teamId, users }: RemoveUsersFromTeamVariables) {
  const response = await put<RemoveUsersFromTeamRequest, RemoveUsersFromTeamResponse>(`/api/teams/${teamId}/users`, { users, action: 'remove' });
  return response.data as RemoveUsersFromTeamResponse;
}

/**
 * Hook for removing users from a team.
 */
export default function useRemoveUsersFromTeam() {
  return useToastedMutation({
    mutationFn: removeUsersFromTeam,
    successToastMessage: (_response, variables: RemoveUsersFromTeamVariables) =>
      `Removed ${variables.users.length} ${plural(variables.users.length, 'user')} from team.`,
    errorToastMessage: 'Failed to remove user(s) from team.',
    onSuccess: (response, variables: RemoveUsersFromTeamVariables) => {
      // Update the team's user count in the cache
      queryClient.setQueryData(getTeamQueryKey(variables.teamId), (oldTeam?: Team) => {
        if (!oldTeam) {
          return oldTeam;
        }
        return {
          ...oldTeam,
          teamUsers: response.users,
        };
      });

      // Also re-fetch the team to get any other changes
      queryClient.invalidateQueries({ queryKey: getTeamQueryKey(variables.teamId) });

      // Invalidate the team members query
      queryClient.invalidateQueries({ queryKey: getBaseTeamMembersQueryKey(variables.teamId) });
    },
  });
}
